import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaStar } from 'react-icons/fa';
import { MdLocationPin } from 'react-icons/md';
import { styled } from 'styled-components';
import ChannelRowItem from '../../../components/ChanneItem/ChannelItem';
import Header from '../../../components/Header/Header';
import { Loading } from '../../../components/Loading/Loading';
import MessageBox from '../../../components/MessageBox/MessageBox';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Text from '../../../components/Text/Text';
import { Env } from '../../../env';
import useRootStore from '../../../hooks/useRootStore';
import { Channel } from '../../../types/channel';
import Colors from '../../../utils/colors';
import styles from './index.module.css';

const container = {
    hidden: { opacity: 1, scale: 0 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            delayChildren: 0.3,
            staggerChildren: 0.2,
        },
    },
};

const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
    },
};

const ChannelsScreenSearch = () => {
    const { t } = useTranslation();
    const { closeModal, toRouter } = useRootStore().routerStore;
    const { myChannels, handelSelectedChannel, getChannelByHashIdForJoin } = useRootStore().channelStore;
    const { openChannel } = useRootStore().chatStore;
    const { searchGroupWorld, clearGlobalGroup, worldGroups, isLoading } = useRootStore().channelProStore;
    const { show, hide } = useRootStore().visibleStore;
    const { clearHashTags } = useRootStore().hashtagStore;

    const serachChannelHandler = (text: string) => {
        searchGroupWorld(text);
    };

    const handleSelectChanel = useCallback(
        (e: Channel) => {
            hide('openHashtag');
            hide('FilterToolbar');
            hide('filterMinRelevance');
            hide('openFooterMediaBar');
            hide('isOpenAllHashtags');
            clearHashTags();

            if (!myChannels.find(channel => channel.id === e.id)?.id) {
                getChannelByHashIdForJoin(e.hashId);
                toRouter('joinToChannel');
            }
        },
        [handelSelectedChannel, openChannel, worldGroups, myChannels],
    );

    const handleCloseModal = () => {
        closeModal('left');
        clearGlobalGroup();
    };

    const toolbarClose = () => {
        hide('openHashtag');
        hide('FilterToolbar');
        hide('filterMinRelevance');
        hide('openFooterMediaBar');
        hide('isOpenAllHashtags');
        clearHashTags();
    };

    return (
        <LeftAreaContainer>
            <Header text={t('world_group')} leftIcon="arrowRight" onLeftIconPress={handleCloseModal} />
            <div className={styles.SearchBox}>
                <SearchInput
                    onClick={toolbarClose}
                    onChange={serachChannelHandler}
                    placeholder={`${t('searchPlaceholder')}`}
                />
            </div>
            <ChannelRowContainer>
                {isLoading ? (
                    <div className={styles.loadingBox}>
                        <Loading />
                    </div>
                ) : (
                    <>
                        {!!(worldGroups?.length === 0) && (
                            <div className={styles.loadingError}>
                                <MessageBox title={t('No Channels')} />
                            </div>
                        )}
                    </>
                )}

                <motion.div variants={container} initial="hidden" animate="visible" className={styles.contentBox}>
                    {worldGroups?.map(e => {
                        return (
                            <motion.div
                                variants={item}
                                whileTap={{ scale: 0.8 }}
                                key={e.id}
                                id="map-dev"
                                className={styles.channelRowBox}
                                onClick={() => handleSelectChanel(e)}
                            >
                                <ChannelRowItem
                                    item={e}
                                    name={e.name}
                                    color={
                                        e.color
                                            ? `linear-gradient(25deg, ${e.color.startsWith('#') ? e.color : '#' + e.color} 30%, #ddd 100%)`
                                            : ''
                                    }
                                    number={e.id}
                                    imageUrl={e.avatar ? `${Env.AssetsUrl}/${e.avatar}` : ''}
                                />
                                <div className={styles.channelNameBox}>
                                    <div>
                                        <Text
                                            children={e.name.length > 30 ? `${e.name.slice(0, 30)}...` : e.name}
                                            color={Colors.White}
                                            fontSize="14px"
                                            margin="0px 0 0 0 "
                                        />
                                        {!!e.rByLocationType?.length && (
                                            <span className={styles.channelLocation}>
                                                <MdLocationPin color={Colors.DarkGray} size={11} />
                                                <Text
                                                    children={'by location'}
                                                    color={Colors.DarkGray}
                                                    fontSize="11px"
                                                    margin="0px 0 0 0 "
                                                />
                                            </span>
                                        )}
                                    </div>
                                    {/* <Text
                                        children={`${e.users?.length} members`}
                                        color={Colors.White}
                                        fontSize="12px"
                                    /> */}
                                </div>
                                <div className={styles.premiumIcon}>
                                    {e.isPremium ? <FaStar size={20} color="#ffdd59" /> : null}
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </ChannelRowContainer>
        </LeftAreaContainer>
    );
};

export default observer(ChannelsScreenSearch);

const LeftAreaContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const ChannelRowContainer = styled.div`
    position: relative;
    flex: 1;
    overflow-y: auto;
    padding-bottom: 20px;
`;
